import {
  Box,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'

import type {
  ExtensionSummary,
  MessageGroupMember,
} from '../api/messagingApi.types'
import type { MessagingTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'N/A'
  }
}

const MinusIcon: React.FC = () => (
  <Icon boxSize={3}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  </Icon>
)

const PlusIcon: React.FC = () => (
  <Icon boxSize={3}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  </Icon>
)

const SearchIcon: React.FC = () => (
  <Icon boxSize={4} color="fg.muted">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  </Icon>
)

export interface MemberManagementProps {
  members: MessageGroupMember[]
  availableMembers: ExtensionSummary[]
  isLoadingMembers: boolean
  isLoadingAvailable: boolean
  onAddMember: (extensionId: number) => void
  onRemoveMember: (memberId: number) => void
  isAdding: boolean
  isRemoving: boolean
  /**
   * When false, hides the per-member remove button and the entire
   * "Available Members" panel (add-member controls). Defaults to `false`
   * — consumers must explicitly opt in to membership management.
   */
  canManage?: boolean
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: MessagingTranslateFn
}

export const MemberManagement: React.FC<MemberManagementProps> = ({
  members,
  availableMembers,
  isLoadingMembers,
  isLoadingAvailable,
  onAddMember,
  onRemoveMember,
  isAdding,
  isRemoving,
  canManage = false,
  t,
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredAvailableMembers = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase()
    if (!trimmed) return availableMembers
    return availableMembers.filter(
      (m) =>
        m.name.toLowerCase().includes(trimmed) ||
        String(m.number).includes(trimmed),
    )
  }, [availableMembers, searchTerm])

  return (
    <Box
      display="flex"
      gap={6}
      flexDirection={{ base: 'column', md: 'row' }}
      minW={0}
    >
      {/* Group Members */}
      <Box flex={1} minW={0}>
        <Heading fontSize="2xl" mb={4}>
          {text(t, 'messaging.members.groupMembersTitle', 'Group Members')}
          {!isLoadingMembers && members.length > 0 && (
            <Text
              as="span"
              fontWeight="normal"
              fontSize="sm"
              color="fg.muted"
              ml={2}
            >
              ({members.length})
            </Text>
          )}
        </Heading>
        {isLoadingMembers ? (
          <Box textAlign="center" py={4}>
            <Spinner size="md" />
          </Box>
        ) : members.length === 0 ? (
          <Text color="fg.muted" fontSize="sm">
            {text(
              t,
              'messaging.members.emptyGroup',
              'No members in this group yet.',
            )}
          </Text>
        ) : (
          <Box overflowX="auto" maxH="500px" overflowY="auto">
            <Table.Root variant="outline" size="sm">
              <Table.Header position="sticky" top={0} zIndex={1} bg="bg">
                <Table.Row bg="bg">
                  <Table.ColumnHeader px={3} py={2}>
                    {text(t, 'messaging.members.columnName', 'Name')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={3} py={2}>
                    {text(t, 'messaging.members.columnExtension', 'Extension')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={3} py={2}>
                    {text(t, 'messaging.members.columnAdded', 'Added')}
                  </Table.ColumnHeader>
                  {canManage && <Table.ColumnHeader px={3} py={2} w="50px" />}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {members.map((member) => (
                  <Table.Row key={member.id}>
                    <Table.Cell px={3} py={2}>
                      <Text fontSize="sm">{member.extension_name}</Text>
                    </Table.Cell>
                    <Table.Cell px={3} py={2}>
                      <Text fontSize="sm">{member.extension_number}</Text>
                    </Table.Cell>
                    <Table.Cell px={3} py={2}>
                      <Text fontSize="sm" color="fg.muted">
                        {formatDate(member.created_at)}
                      </Text>
                    </Table.Cell>
                    {canManage && (
                      <Table.Cell px={3} py={2}>
                        <IconButton
                          aria-label={text(
                            t,
                            'messaging.members.removeMemberAria',
                            'Remove member',
                          )}
                          size="xs"
                          colorPalette="error"
                          variant="subtle"
                          onClick={() => onRemoveMember(member.id)}
                          disabled={isRemoving}
                          borderRadius="full"
                        >
                          <MinusIcon />
                        </IconButton>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Box>

      {/* Available Members — hidden entirely for users without manage permission */}
      {canManage && (
        <Box flex={1} minW={0} data-testid="messaging-available-members">
          <Heading fontSize="2xl" mb={4}>
            {text(
              t,
              'messaging.members.availableMembersTitle',
              'Available Members',
            )}
            {!isLoadingAvailable && availableMembers.length > 0 && (
              <Text
                as="span"
                fontWeight="normal"
                fontSize="sm"
                color="fg.muted"
                ml={2}
              >
                ({availableMembers.length})
              </Text>
            )}
          </Heading>
          {isLoadingAvailable ? (
            <Box textAlign="center" py={4}>
              <Spinner size="md" />
            </Box>
          ) : availableMembers.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              {text(
                t,
                'messaging.members.emptyAvailable',
                'No available members to add.',
              )}
            </Text>
          ) : (
            <>
              <InputGroup mb={3} endElement={<SearchIcon />}>
                <Input
                  size="sm"
                  placeholder={text(
                    t,
                    'messaging.members.searchPlaceholder',
                    'Search by name or extension...',
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Box overflowX="auto" maxH="500px" overflowY="auto">
                <Table.Root variant="outline" size="sm">
                  <Table.Header position="sticky" top={0} zIndex={1} bg="bg">
                    <Table.Row bg="bg">
                      <Table.ColumnHeader px={3} py={2}>
                        {text(t, 'messaging.members.columnName', 'Name')}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={3} py={2}>
                        {text(
                          t,
                          'messaging.members.columnExtension',
                          'Extension',
                        )}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={3} py={2} w="50px" />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredAvailableMembers.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={3} px={3} py={4}>
                          <Text
                            fontSize="sm"
                            color="fg.muted"
                            textAlign="center"
                          >
                            {text(
                              t,
                              'messaging.members.noSearchResults',
                              'No members match your search.',
                            )}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      filteredAvailableMembers.map((member) => (
                        <Table.Row key={member.id}>
                          <Table.Cell px={3} py={2}>
                            <Text fontSize="sm">{member.name}</Text>
                          </Table.Cell>
                          <Table.Cell px={3} py={2}>
                            <Text fontSize="sm">{member.number}</Text>
                          </Table.Cell>
                          <Table.Cell px={3} py={2}>
                            <IconButton
                              aria-label={text(
                                t,
                                'messaging.members.addMemberAria',
                                'Add member',
                              )}
                              size="xs"
                              colorPalette="brand"
                              variant="subtle"
                              onClick={() => onAddMember(member.id)}
                              disabled={isAdding}
                              borderRadius="full"
                            >
                              <PlusIcon />
                            </IconButton>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
